import { Box } from '@deckee/deck-hand';
import { WaterBody } from '@deckee/utilities';
import type {
  BoatingRiskRating,
  HourlyMarineConditions,
  VesselGraphic,
} from '@deckee/utilities';
import { DateTime } from 'luxon';
import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { trackWeatherForecastNearbyStationSelected } from '../../helpers/analytics/weatherForecastAnalytics';
import { getWeatherForecastHomePath } from '../../helpers/location/searchPaths';
import {
  getBoatingForecastDescription,
  INLAND_NEAREST_COASTAL_PREFIX,
} from '../../helpers/risk/rideCopy';
import { useNearbyWeatherStations } from '../../hooks/useNearbyWeatherStations';
import StationLink from '../StationLink/StationLink';
import { getRiskForecastLevelDisplay } from './getRiskForecastLevelDisplay';

interface RiskForecastDescriptionProps {
  conditions: HourlyMarineConditions;
  graphic: VesselGraphic;
  rating: BoatingRiskRating;
  waterBody: WaterBody;
  selectedGraphTime: string;
  latitude: number;
  longitude: number;
  selectedDate: string;
  locationTimezone?: string;
  riskLevel?: string;
  /** When true, only the description is shown (risk label rendered elsewhere). */
  hideHeader?: boolean;
}

const RiskForecastDescription: FC<RiskForecastDescriptionProps> = ({
  conditions,
  graphic,
  rating,
  waterBody,
  selectedGraphTime,
  latitude,
  longitude,
  selectedDate,
  locationTimezone,
  riskLevel,
  hideHeader = false,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const isInland = waterBody !== WaterBody.Coast;
  const { stations } = useNearbyWeatherStations(
    latitude,
    longitude,
    'swell',
    isInland,
  );
  const nearestCoastalStation = stations[0];
  const selectedDateTime = DateTime.fromISO(selectedGraphTime);

  let formattedSelectedDay = '';
  let formattedSelectedTime = '';
  if (selectedDateTime.isValid) {
    formattedSelectedDay = selectedDateTime
      .setZone(locationTimezone)
      .toFormat('ccc');
    formattedSelectedTime = selectedDateTime
      .setZone(locationTimezone)
      .toFormat('h:mma');
  }

  const riskLevelDisplay = getRiskForecastLevelDisplay(riskLevel, theme);

  const handleNearestCoastalClick = () => {
    if (!nearestCoastalStation) {
      return;
    }

    trackWeatherForecastNearbyStationSelected({
      chartLabel: 'Boating forecast',
      stationType: 'swell',
      latitude: nearestCoastalStation.latitude,
      longitude: nearestCoastalStation.longitude,
      selectedDate,
      stationName: nearestCoastalStation.name,
    });

    history.push(
      getWeatherForecastHomePath(
        nearestCoastalStation.latitude,
        nearestCoastalStation.longitude,
        selectedDate,
      ),
    );
  };

  return (
    <>
      {!hideHeader && (
        <Box fontWeight="bold" mb="2" fontSize="3">
          <Box as="span">
            {`${formattedSelectedDay} ${formattedSelectedTime} `}
          </Box>
          <Box as="span" color={riskLevelDisplay?.color}>
            {riskLevelDisplay?.label}
          </Box>
        </Box>
      )}

      <Box borderRadius="5px" width="100%" fontSize={1}>
        {getBoatingForecastDescription(conditions, graphic, rating, waterBody)}
        {isInland && nearestCoastalStation ? (
          <>
            {` ${INLAND_NEAREST_COASTAL_PREFIX} `}
            <StationLink
              color="primary"
              fontWeight="bold"
              name={nearestCoastalStation.shortName}
              onClick={handleNearestCoastalClick}
            />
            .
          </>
        ) : null}
      </Box>
    </>
  );
};

RiskForecastDescription.defaultProps = {
  locationTimezone: undefined,
  riskLevel: undefined,
};

export default RiskForecastDescription;
